import { useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { AlertCircle, Search } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { Alert, AlertDescription, AlertTitle } from '@quebon/ui/components/alert'
import { Badge } from '@quebon/ui/components/badge'
import { Button } from '@quebon/ui/components/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@quebon/ui/components/card'
import { Checkbox } from '@quebon/ui/components/checkbox'
import { Field, FieldGroup, FieldLabel } from '@quebon/ui/components/field'
import { Input } from '@quebon/ui/components/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@quebon/ui/components/select'
import { Skeleton } from '@quebon/ui/components/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@quebon/ui/components/table'

import {
  adminApi,
  type AdminLcCenterDashboardRow,
  type AdminLcCenterStatus,
} from '@/api/admin'

const STATUS_LABEL: Record<AdminLcCenterStatus, string> = {
  EDUCATION: '교육',
  OPERATING: '운영',
  TERMINATED: '해지',
  ON_HOLD: '보류',
}

const STATUS_VARIANT: Record<AdminLcCenterStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  EDUCATION: 'secondary',
  OPERATING: 'default',
  TERMINATED: 'destructive',
  ON_HOLD: 'outline',
}

const WON_FORMATTER = new Intl.NumberFormat('ko-KR')

function formatWon(amount: number | null): string {
  if (amount === null || !Number.isFinite(amount)) return '—'
  return `${WON_FORMATTER.format(amount)}원`
}

interface LcCenterTableProps {
  rows: AdminLcCenterDashboardRow[]
  onOpenContract: (organizationId: number) => void
}

export function LcCenterTable({ rows, onOpenContract }: LcCenterTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>도메인</TableHead>
          <TableHead>센터</TableHead>
          <TableHead>지역</TableHead>
          <TableHead>원장</TableHead>
          <TableHead className="text-right">예비원생</TableHead>
          <TableHead className="text-right">재원생</TableHead>
          <TableHead className="text-right">클래스</TableHead>
          <TableHead>상태</TableHead>
          <TableHead>개원일</TableHead>
          <TableHead className="text-right">가맹비</TableHead>
          <TableHead className="text-right">콘텐츠</TableHead>
          <TableHead className="text-right">깨봉몰</TableHead>
          <TableHead className="text-right">매출합계</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={13} className="h-28 text-center text-muted-foreground">
              조건에 맞는 러닝센터가 없습니다.
            </TableCell>
          </TableRow>
        ) : rows.map((row) => (
          <TableRow key={row.organizationId}>
            <TableCell className="font-mono text-xs">{row.subdomain ?? '—'}</TableCell>
            <TableCell className="font-medium">{row.centerName}</TableCell>
            <TableCell>{row.region ?? '—'}</TableCell>
            <TableCell>
              <div className="flex flex-col gap-0.5">
                <span>{row.ownerName ?? '—'}</span>
                <span className="text-xs text-muted-foreground">{row.ownerPhoneNumber ?? '—'}</span>
              </div>
            </TableCell>
            <TableCell className="text-right tabular-nums">{row.waitingLearners}명</TableCell>
            <TableCell className="text-right tabular-nums">{row.enrolledLearners}명</TableCell>
            <TableCell className="text-right tabular-nums">{row.activeClasses}개</TableCell>
            <TableCell>
              <Badge variant={STATUS_VARIANT[row.status]}>{STATUS_LABEL[row.status]}</Badge>
            </TableCell>
            <TableCell className="tabular-nums">{row.openedOn ?? '—'}</TableCell>
            <TableCell className="text-right tabular-nums">
              {row.franchiseFeeAmount === null ? '—' : (
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  onClick={() => onOpenContract(row.organizationId)}
                >
                  {formatWon(row.franchiseFeeAmount)}
                </Button>
              )}
            </TableCell>
            <TableCell className="text-right tabular-nums">{formatWon(row.contentRevenueAmount)}</TableCell>
            <TableCell className="text-right tabular-nums">{formatWon(row.mallRevenueAmount)}</TableCell>
            <TableCell className="text-right font-medium tabular-nums">{formatWon(row.totalRevenueAmount)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function LoadingTable() {
  return (
    <div className="flex flex-col gap-2" aria-label="LC 현황을 불러오는 중">
      {Array.from({ length: 6 }, (_, index) => (
        <Skeleton key={index} className="h-11 w-full" />
      ))}
    </div>
  )
}

export function LcCenterDashboard() {
  const navigate = useNavigate()
  const [keywordInput, setKeywordInput] = useState('')
  const [keyword, setKeyword] = useState('')
  const [status, setStatus] = useState<AdminLcCenterStatus | undefined>()
  const [includeTest, setIncludeTest] = useState(false)
  const [page, setPage] = useState(0)

  const query = useQuery({
    queryKey: ['admin', 'dashboard', 'lc-centers', { page, keyword, status, includeTest }],
    queryFn: () => adminApi.getLcCenterDashboard({
      page,
      size: 20,
      keyword: keyword || undefined,
      status,
      includeTest,
    }),
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  })

  const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setKeyword(keywordInput.trim())
    setPage(0)
  }

  const resetFilters = () => {
    setKeywordInput('')
    setKeyword('')
    setStatus(undefined)
    setIncludeTest(false)
    setPage(0)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>러닝센터 운영·매출 현황</CardTitle>
        <CardDescription>
          상태와 개원일은 기관 관리의 계약 탭에서 관리하며, 매출은 환불액을 제외한 누계입니다.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <form onSubmit={submitSearch}>
          <FieldGroup className="@md/field-group:flex-row @md/field-group:items-end @md/field-group:gap-2">
            <Field className="max-w-sm">
              <FieldLabel htmlFor="lc-center-search">센터 검색</FieldLabel>
              <Input
                id="lc-center-search"
                value={keywordInput}
                onChange={(event) => setKeywordInput(event.target.value)}
                placeholder="센터명, 도메인, 주소"
              />
            </Field>
            <Field className="w-full @md/field-group:w-40">
              <FieldLabel htmlFor="lc-center-status">상태</FieldLabel>
              <Select
                value={status ?? 'ALL'}
                onValueChange={(value) => {
                  setStatus(value && value !== 'ALL' ? value as AdminLcCenterStatus : undefined)
                  setPage(0)
                }}
              >
                <SelectTrigger id="lc-center-status" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="ALL">전체</SelectItem>
                    <SelectItem value="EDUCATION">교육</SelectItem>
                    <SelectItem value="OPERATING">운영</SelectItem>
                    <SelectItem value="TERMINATED">해지</SelectItem>
                    <SelectItem value="ON_HOLD">보류</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </Field>
            <Field orientation="horizontal" className="w-auto self-end pb-1.5">
              <Checkbox
                id="lc-center-include-test"
                checked={includeTest}
                onCheckedChange={(checked) => {
                  setIncludeTest(checked)
                  setPage(0)
                }}
              />
              <FieldLabel htmlFor="lc-center-include-test" className="whitespace-nowrap">
                테스트 계정 포함
              </FieldLabel>
            </Field>
            <div className="flex gap-2">
              <Button type="submit">
                <Search data-icon="inline-start" />
                조회
              </Button>
              <Button type="button" variant="outline" onClick={resetFilters}>초기화</Button>
            </div>
          </FieldGroup>
        </form>

        {query.isError ? (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertTitle>LC 현황을 불러오지 못했습니다.</AlertTitle>
            <AlertDescription>잠시 후 다시 조회해주세요.</AlertDescription>
          </Alert>
        ) : query.isLoading ? (
          <LoadingTable />
        ) : (
          <>
            <div className="text-sm text-muted-foreground">
              총 <span className="font-medium text-foreground tabular-nums">{query.data?.totalElements ?? 0}</span>개 센터
            </div>
            <LcCenterTable
              rows={query.data?.content ?? []}
              onOpenContract={(organizationId) => navigate(`/organizations/${organizationId}?tab=contract`)}
            />
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm text-muted-foreground tabular-nums">
                {(query.data?.totalPages ?? 0) === 0 ? 0 : page + 1} / {query.data?.totalPages ?? 0} 페이지
              </span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={query.data?.first ?? true}
                  onClick={() => setPage((current) => Math.max(0, current - 1))}
                >
                  이전
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={query.data?.last ?? true}
                  onClick={() => setPage((current) => current + 1)}
                >
                  다음
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
